import { useEffect, useRef, useState } from "react";
import { Download, Trash2, UploadCloud, FileSignature, FolderOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LIVRABLE_PHASES, fetchLivrables, uploadLivrable, deleteDocument, getDownloadUrl, lastModifiedLabel } from "@/lib/dataroom";

function PhaseDropzone({ phase, onUpload }) {
    const inputRef = useRef(null);
    const [requiresSig, setRequiresSig] = useState(false);
    const [busy, setBusy] = useState(false);
    const [drag, setDrag] = useState(false);
    async function send(fileList) {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setBusy(true);
        for (const f of files) { try { await onUpload(f, phase.key, requiresSig); } catch (e) { toast.error(`Échec : ${f.name}`, { description: e.message }); } }
        setBusy(false); setRequiresSig(false);
    }
    return (
        <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid={`livrable-sig-check-${phase.key}`} checked={requiresSig} onChange={(e) => setRequiresSig(e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300" />
                Nécessite une signature physique
            </label>
            <div data-testid={`livrable-dropzone-${phase.key}`} role="button" tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }}
                className={`rounded-md border border-dashed px-4 py-4 flex flex-col items-center text-center cursor-pointer text-xs transition-colors ${drag ? "border-primary bg-accent" : "border-slate-300 hover:bg-accent/40"} ${busy ? "pointer-events-none opacity-60" : ""}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <UploadCloud className="h-4 w-4 text-primary mb-1" />}
                <span className="text-slate-600">{busy ? "Publication…" : "Déposer / publier au client"}</span>
                <input ref={inputRef} type="file" multiple className="hidden" data-testid={`livrable-input-${phase.key}`} onChange={(e) => { send(e.target.files); e.target.value = ""; }} />
            </div>
        </div>
    );
}

function LivrableFile({ doc, mode, onDownload, onDelete, onUploadSigned }) {
    const signedInput = useRef(null);
    const [busy, setBusy] = useState(false);
    const needsSig = doc.requires_signature && !doc.signe;
    const signed = doc.requires_signature && doc.signe;
    async function sendSigned(fileList) {
        const f = Array.from(fileList || [])[0];
        if (!f) return;
        setBusy(true);
        try { await onUploadSigned(doc, f); } catch (e) { toast.error("Dépôt impossible", { description: e.message }); }
        setBusy(false);
    }
    return (
        <li data-testid={`livrable-file-${doc.id}`} className="px-4 py-3 space-y-2">
            <div className="flex items-center gap-3 text-sm">
                <FileSignature className="h-4 w-4 text-indigo-600 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0 flex-1">
                    <span className="block truncate">{doc.nom_fichier}</span>
                    <span className="text-xs text-slate-500">Dernière modification le {lastModifiedLabel(doc.created_at)}</span>
                </div>
                {needsSig && <span data-testid={`livrable-badge-tosign-${doc.id}`} className="shrink-0 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700">⚠️ À imprimer, parapher et signer</span>}
                {signed && <span data-testid={`livrable-badge-signed-${doc.id}`} className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">✅ Signé</span>}
                <button type="button" data-testid={`livrable-download-${doc.id}`} onClick={() => onDownload(doc)} className="text-slate-400 hover:text-primary" title="Télécharger"><Download className="h-4 w-4" /></button>
                {mode === "admin" && onDelete && <button type="button" data-testid={`livrable-delete-${doc.id}`} onClick={() => onDelete(doc)} className="text-slate-300 hover:text-rose-600" title="Supprimer"><Trash2 className="h-4 w-4" /></button>}
            </div>
            {mode === "client" && needsSig && onUploadSigned && (
                <div data-testid={`livrable-sign-drop-${doc.id}`} role="button" tabIndex={0} onClick={() => signedInput.current?.click()}
                    className="ml-7 rounded-md border border-dashed border-rose-300 bg-rose-50/50 px-3 py-2 text-xs font-medium text-rose-700 cursor-pointer hover:bg-rose-50">
                    {busy ? "Dépôt de la version signée…" : "📥 Déposer la version signée"}
                    <input ref={signedInput} type="file" accept=".pdf" className="hidden" data-testid={`livrable-sign-input-${doc.id}`} onChange={(e) => { sendSigned(e.target.files); e.target.value = ""; }} />
                </div>
            )}
        </li>
    );
}

export default function LivrablesSection({ mode = "admin", dossierId, isResto = false, livrables: livrablesProp, onUploadSigned, onDownload: onDownloadProp, refreshToken = 0 }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(mode === "admin");
    useEffect(() => {
        if (mode !== "admin" || !dossierId) return;
        setLoading(true);
        fetchLivrables(dossierId).then(setDocs).catch(() => setDocs([])).finally(() => setLoading(false));
    }, [mode, dossierId, refreshToken]);

    if (!isResto) return null;
    const livrables = mode === "admin" ? docs : (livrablesProp || []);

    async function handleUpload(file, phaseKey, requiresSig) {
        const doc = await uploadLivrable(dossierId, file, phaseKey, requiresSig);
        setDocs((d) => [doc, ...d]);
        toast.success("Livrable publié au client", { description: file.name });
    }
    async function handleDelete(doc) {
        try { await deleteDocument(doc); setDocs((d) => d.filter((x) => x.id !== doc.id)); toast.success("Livrable supprimé"); }
        catch (e) { toast.error("Suppression impossible", { description: e.message }); }
    }
    async function handleDownloadAdmin(doc) {
        try { window.open(await getDownloadUrl(doc.url_stockage), "_blank", "noopener,noreferrer"); }
        catch (e) { toast.error("Téléchargement impossible", { description: e.message }); }
    }
    const onDownload = mode === "admin" ? handleDownloadAdmin : onDownloadProp;

    return (
        <section data-testid="livrables-section" className="space-y-5 rounded-2xl border border-indigo-200 bg-indigo-50/30 p-6">
            <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center"><FolderOpen className="h-4 w-4 text-primary" strokeWidth={1.75} /></div>
                <div>
                    <h2 className="font-display text-xl text-foreground">Documents téléchargeables (Livrables du Cabinet)</h2>
                    <p className="text-xs text-muted-foreground">{mode === "admin" ? "Publiez vos livrables définitifs par phase. Cochez « Nécessite signature » pour déclencher la boucle de signature client." : "Documents officiels transmis par votre conseil. Imprimez, signez et redéposez les pièces marquées en rouge."}</p>
                </div>
            </div>
            {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
                <div className="grid gap-4 md:grid-cols-2">
                    {LIVRABLE_PHASES.map((phase) => {
                        const sub = livrables.filter((d) => (d.livrable_phase || "2-audience-ouverture") === phase.key);
                        return (
                            <div key={phase.key} data-testid={`livrable-phase-${phase.key}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                                <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5 rounded-t-xl">
                                    <h3 className="text-sm font-semibold text-slate-700">{phase.label}</h3>
                                    <span className="ml-auto text-xs text-slate-500">{sub.length} fichier{sub.length > 1 ? "s" : ""}</span>
                                </div>
                                {sub.length === 0 ? <p className="px-4 py-3 text-xs text-slate-400">Aucun livrable publié pour cette phase.</p> : (
                                    <ul className="divide-y divide-slate-100">
                                        {sub.map((doc) => <LivrableFile key={doc.id} doc={doc} mode={mode} onDownload={onDownload} onDelete={mode === "admin" ? handleDelete : undefined} onUploadSigned={onUploadSigned} />)}
                                    </ul>
                                )}
                                {mode === "admin" && <div className="border-t border-slate-100 px-4 py-3"><PhaseDropzone phase={phase} onUpload={handleUpload} /></div>}
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
